/**
 * EditorialStrip / "The Latest" — homepage horizontal-scroll rail of recent work.
 * Covers get their own section (CoversStrip); this rail shows campaigns +
 * editorials, each fully credited and linking to its /news/{id} detail page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { compactCredits } from "../lib/usePageMeta";

export default function EditorialStrip() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/latest")
      .then((r) => setItems((Array.isArray(r.data) ? r.data : []).filter((w) => w.type !== "cover")))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <section
      data-testid="editorial-strip"
      className="border-t border-black/10 bg-white py-14 lg:py-20"
    >
      <div className="max-w-[1800px] mx-auto">
        <div className="px-6 lg:px-12 mb-8 lg:mb-12 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="label-overline text-zinc-500">The latest / 01</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light mt-2 leading-[0.95]">
              The Latest<span className="italic text-zinc-500">.</span>
            </h2>
          </div>
          <Link
            to="/archive"
            data-testid="latest-archive-link"
            className="label-overline text-zinc-500 hover:text-black transition-colors"
          >
            View the archive +
          </Link>
        </div>

        <div className="overflow-x-auto no-scrollbar snap-x snap-mandatory">
          <div className="flex gap-4 md:gap-6 px-6 lg:px-12 pb-4 min-w-max">
            {items.map((it) => (
              <article
                key={it.id}
                data-testid={`latest-tile-${it.id}`}
                className="snap-start shrink-0 w-[78vw] sm:w-[50vw] md:w-[38vw] lg:w-[28vw] xl:w-[22vw] max-w-[420px]"
              >
                <Link to={`/news/${it.id}`} className="block group focus:outline-none focus:ring-1 focus:ring-black">
                  <div className="relative w-full aspect-[3/4] bg-[#F5F5F5] overflow-hidden">
                    <img
                      src={it.image_url}
                      alt={`${it.brand}${it.subtitle ? " — " + it.subtitle : ""}`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                  </div>
                  <div className="mt-4">
                    {it.model_name && (
                      <p className="font-serif text-base leading-tight text-zinc-800">{it.model_name}</p>
                    )}
                    <p className="font-serif text-xl leading-tight mt-0.5">
                      {it.brand}
                      {it.subtitle && (
                        <span className="italic text-zinc-500"> — {it.subtitle}</span>
                      )}
                    </p>
                    {(it.photographer || it.stylist || it.hair || it.makeup) ? (
                      <p className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500 leading-relaxed line-clamp-2">
                        {[
                          it.photographer && `Photographed by ${it.photographer}`,
                          it.stylist && `Styled by ${it.stylist}`,
                          it.hair && `Hair by ${it.hair}`,
                          it.makeup && `Makeup by ${it.makeup}`,
                        ].filter(Boolean).join(", ")}
                      </p>
                    ) : it.caption ? (
                      <p className="mt-2 text-[11px] uppercase tracking-widest text-zinc-500 leading-relaxed">
                        {it.caption}
                      </p>
                    ) : null}
                  </div>
                </Link>
              </article>
            ))}

          </div>
        </div>
      </div>
    </section>
  );
}
